import React, { useEffect, useState } from "react";
import { DatePicker, InputNumber } from "antd";
import dayjs from "dayjs";
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../../consts";
import { LoadingIcon } from "../../../components";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { fetchTemperatureData, getTodayDate } from "./utils";
import { useUser } from "../../../context/UserContext";
import { positiveTrendFlagOn } from "../../../utils/positiveTrendCorrection";

dayjs.extend(isSameOrAfter);

const { RangePicker } = DatePicker;

const Forecast = ({
  currentSite,
  siteMetadata,
  currentGreenhouse,
  averageData,
  tableData,
  setForecastLeafSizeLength,
}) => {
  const GRAPH_UI_DATE_FORMAT = "DD MMM YY";

  // OF-129 gate — mirrors AverageGraph/DistributionGraph: only non-admins on a
  // site+crop opted in via FORCE_POSITIVE_LEAF_TREND get the corrected growth.
  const { isAdmin } = useUser();
  const admin = typeof isAdmin === "function" ? isAdmin() : false;
  const applyTrendCorrection = !admin && positiveTrendFlagOn(siteMetadata);

  const [averageDifferenceForRange, setAverageDifferenceForRange] =
    useState(null);
  const [forecastDays, setForecastDays] = useState(4);
  const [gddBaseTemperature, setGddBaseTemperature] = useState(0);
  const [idealHarvestLeafSize, setIdealHarvestLeafSize] = useState(7);
  const [gdd, setGdd] = useState(0);
  const [selectedRangeGdd, setSelectedRangeGdd] = useState(null);
  const [forecastGdd, setForecastGdd] = useState(0);

  const [plantingDate, setPlantingDate] = useState(null);
  const [harvestDate, setHarvestDate] = useState(null);

  const [selectedGDDDates, setSelectedGDDDates] = useState([]);
  const [latLong, setLatLong] = useState(null);
  const [forecastLeafSizeFor5Days, setForecastLeafSizeFor5Days] =
    useState(null);

  useEffect(() => {
    if (currentSite) {
      setLatLong(`${currentSite.lat},${currentSite.long}`);
    }
  }, [currentSite]);

  useEffect(() => {
    if (siteMetadata) {
      const baseTemp = siteMetadata["GDD_BASE_TEMPERATURE_IN_CELSIUS"] ?? 0;
      const idealSize =
        siteMetadata["IDEAL_HARVEST_LEAF_SIZE_FOR_FORECAST"] ?? 7;
      setGddBaseTemperature(baseTemp);
      setIdealHarvestLeafSize(idealSize);
    }
  }, [siteMetadata]);

  useEffect(() => {
    if (!tableData?.[0]) return;
    if (tableData?.[0]?.bay != currentGreenhouse?.name) return;

    setPlantingDate(tableData[0].plantingDate || null);
    setHarvestDate(tableData[0].harvestDate || null);
  }, [tableData, currentGreenhouse]);

  // ──────────────────────────────────────────────────────────
  // Calc diff on range / averageData change
  // ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (selectedGDDDates?.length !== 2) return;
    if (!averageData?.length) return;
    handleGDDRangeChange(selectedGDDDates, true);
    // applyTrendCorrection included so the growth recomputes if the gate flips
    // (e.g. a late-arriving admin profile) rather than showing a stale value.
  }, [selectedGDDDates, averageData, applyTrendCorrection]);

  // Default range: planting → today / harvest
  useEffect(() => {
    if (plantingDate) {
      const end =
        harvestDate && dayjs(harvestDate).isBefore(dayjs())
          ? dayjs(harvestDate)
          : getTodayDate(latLong);
      const defaultRange = [dayjs(plantingDate), end];
      handleGDDRangeChange(defaultRange, true);
      setSelectedGDDDates(defaultRange);
    }
  }, [plantingDate, harvestDate, latLong]);

  // Forecast-GDD input box
  useEffect(() => {
    if (forecastDays && latLong) {
      handleGDDForecastChange(forecastDays);
    }
  }, [forecastDays, latLong]);

  const calculateGDD = (temperatureData, baseTemp = gddBaseTemperature) => {
    let total = 0;
    if (temperatureData) {
      for (let day of temperatureData) {
        const daily = (day.max + day.min) / 2 - baseTemp;
        total += daily > 0 ? daily : 0;
      }
    }
    return total;
  };

  const getSortedFilteredDatesRange = (dates, excludeDates = []) => {
    if (!averageData.length || dates.length !== 2) return null;
    const [startD, endD] = dates;
    let filtered = averageData.filter(({ date }) => {
      const dd = dayjs(date, GRAPH_UI_DATE_FORMAT);
      return (
        dd.isBetween(startD, endD, null, "[]") &&
        !excludeDates.includes(dd.format(BACKEND_DATE_FORMAT))
      );
    });
    filtered = filtered.filter(({ averageLength, averagePercentile80_95 }) => {
      return (
        averageLength !== null &&
        averageLength !== undefined &&
        averagePercentile80_95 !== null &&
        averagePercentile80_95 !== undefined &&
        !isNaN(averageLength) &&
        !isNaN(averagePercentile80_95)
      );
    });
    if (filtered.length < 2) return null;
    return filtered.sort((a, b) =>
      dayjs(a.date, GRAPH_UI_DATE_FORMAT).diff(
        dayjs(b.date, GRAPH_UI_DATE_FORMAT),
      ),
    );
  };

  const calculateDifferenceForDateRange = (arr, key = "averageLength") => {
    if (!arr || arr.length < 2) return null;
    const leftmost = arr[0][key];
    const rightmost = arr[arr.length - 1][key];
    const rawDiff = rightmost - leftmost;
    // OF-129: the corrected (customer) view reorders the trend ascending, so the
    // growth it shows over any range is max - min of that range. Only kicks in
    // when the raw endpoint growth is negative (same rule as the trend line, and
    // Decision A "only when net-negative"). Recomputes naturally on range change
    // — it's just the min/max of whatever values are in range. Admins/non-opted
    // keep the existing raw behaviour (negative clamped to 0).
    if (applyTrendCorrection && rawDiff < 0) {
      const vals = arr.map((d) => d[key]).filter((v) => Number.isFinite(v));
      if (vals.length >= 2) return Math.max(...vals) - Math.min(...vals);
    }
    return rawDiff > 0 ? rawDiff : 0;
  };

  const handleGDDRangeChange = async (dates, isInitial = false) => {
    if (!dates || dates.length !== 2) return;
    setSelectedGDDDates(dates);

    const [start, end] = dates;
    const sortedFiltered = getSortedFilteredDatesRange(dates);
    const diff = calculateDifferenceForDateRange(sortedFiltered);
    setAverageDifferenceForRange(diff);

    const tempData = await fetchTemperatureData(
      latLong,
      start.format(BACKEND_DATE_FORMAT),
      end.format(BACKEND_DATE_FORMAT),
      siteMetadata,
    );
    const gddVal = calculateGDD(tempData);
    setSelectedRangeGdd(gddVal);

    if (typeof isInitial === "boolean" && isInitial) {
      if (!sortedFiltered) {
        setForecastLeafSizeFor5Days("INSUFFICIENT_DATA");
        setForecastLeafSizeLength([]);
        return;
      }
      const excludeDates =
        currentGreenhouse?.metadata?.FORECAST_EXCLUDE_DATE || [];
      let sortedForecast = sortedFiltered;
      let finalDiff = diff;
      if (excludeDates?.length) {
        sortedForecast = getSortedFilteredDatesRange(dates, excludeDates);
        finalDiff = calculateDifferenceForDateRange(sortedForecast);
      }
      // filter temperature data to match final range
      const inRangeTemp = tempData.filter(({ date }) =>
        dayjs(date, BACKEND_DATE_FORMAT).isBetween(
          dayjs(sortedForecast[0].date, GRAPH_UI_DATE_FORMAT),
          dayjs(
            sortedForecast[sortedForecast.length - 1].date,
            GRAPH_UI_DATE_FORMAT,
          ),
          null,
          "[]",
        ),
      );
      const totalAvgP80_95 = calculateDifferenceForDateRange(
        sortedForecast,
        "averagePercentile80_95",
      );
      const lastAvgP80_95 =
        sortedFiltered[sortedFiltered.length - 1]?.averagePercentile80_95 ?? 0;

      updateForecastLeafSize(
        end,
        finalDiff,
        totalAvgP80_95,
        lastAvgP80_95,
        calculateGDD(inRangeTemp),
      );
    }
  };

  const handleGDDForecastChange = async (val) => {
    setForecastDays(val);
    const tomorrow = getTodayDate(latLong)
      .add(1, "day")
      .format(BACKEND_DATE_FORMAT);
    const forecastEnd = getTodayDate(latLong)
      .add(val, "day")
      .format(BACKEND_DATE_FORMAT);
    const tempData = await fetchTemperatureData(
      latLong,
      tomorrow,
      forecastEnd,
      siteMetadata,
    );
    const total = calculateGDD(tempData);
    setForecastGdd(total);
  };

  const updateForecastLeafSize = async (
    endMoment,
    totalAverage,
    totalAverageP80_95,
    lastAverageP80_95,
    gddResult,
  ) => {
    if (!averageData?.length) {
      setForecastLeafSizeFor5Days("N/A");
      setForecastLeafSizeLength([]);
      return;
    }

    const lastDate = averageData[averageData.length - 1]?.date;
    if (!lastDate) {
      setForecastLeafSizeFor5Days("N/A");
      setForecastLeafSizeLength([]);
      return;
    }

    const lastMoment = dayjs(lastDate, GRAPH_UI_DATE_FORMAT);
    const fcStart = lastMoment.add(1, "day").format(BACKEND_DATE_FORMAT);
    const fcEnd = lastMoment.add(5, "day").format(BACKEND_DATE_FORMAT);

    const data5 = await fetchTemperatureData(
      latLong,
      fcStart,
      fcEnd,
      siteMetadata,
    );
    const gdd5 = calculateGDD(data5);

    const fcLeafSize = ((totalAverageP80_95 ?? 0) * gdd5) / (gddResult || 1);

    if (lastAverageP80_95 + fcLeafSize >= idealHarvestLeafSize) {
      setForecastLeafSizeFor5Days(fcLeafSize);
    } else {
      setForecastLeafSizeFor5Days("EARLY");
    }

    generateEachDayForecast(data5, totalAverage, totalAverageP80_95, gddResult);
  };

  const generateEachDayForecast = (
    temperatureData,
    totalAverage,
    totalAverageP80_95,
    gddRangeVal,
  ) => {
    if (!temperatureData) return;
    let runningGDD = 0;
    const result = [];

    for (let item of temperatureData) {
      runningGDD += calculateGDD([item]);
      const incAvg = ((totalAverage ?? 0) * runningGDD) / (gddRangeVal || 1);
      const incP80_95 =
        ((totalAverageP80_95 ?? 0) * runningGDD) / (gddRangeVal || 1);
      result.push({
        date: dayjs(item.date).format(GRAPH_UI_DATE_FORMAT),
        averageLength: incAvg.toFixed(1),
        averagePercentile80_95: incP80_95.toFixed(1),
      });
    }
    setForecastLeafSizeLength(result);
  };

  const disabledDate = (current) => {
    // Disallow picking before plantingDate or after today
    return (
      current &&
      (current < dayjs(plantingDate) ||
        current > getTodayDate(latLong).endOf("day"))
    );
  };

  let leafSizeContent;
  if (forecastLeafSizeFor5Days === "EARLY") {
    leafSizeContent = null;
  } else if (forecastLeafSizeFor5Days === "N/A") {
    leafSizeContent = (
      <div className="text-darkerGray mb-3">
        No forecast available (crop is already harvested or no data).
      </div>
    );
  } else if (forecastLeafSizeFor5Days === "INSUFFICIENT_DATA") {
    leafSizeContent = (
      <div className="text-darkerGray mb-3">
        Insufficient data to calculate forecast leaf size.
      </div>
    );
  } else if (forecastLeafSizeFor5Days != null) {
    leafSizeContent = (
      <div className="mb-3">
        <span className="font-bold">
          Forecast Average Increase in Leaf Size (Next 5 Days):{" "}
        </span>
        {typeof forecastLeafSizeFor5Days === "number"
          ? forecastLeafSizeFor5Days.toFixed(1) + " cm"
          : forecastLeafSizeFor5Days}
      </div>
    );
  } else {
    leafSizeContent = (
      <LoadingIcon height="50px" classname="!h-4" customHeightClass={true} />
    );
  }

  return (
    <div className="forecast-card w-full mb-5 ">
      <h2 className="font-bold text-lg mb-3">GDD and Growth Trend</h2>
      <div className="flex items-center sm:items-start justify-between mb-3 sm:flex-col">
        <div>
          <span>GDD from &nbsp;</span>
          <RangePicker
            value={selectedGDDDates}
            onChange={handleGDDRangeChange}
            disabledDate={disabledDate}
            format={UI_DATE_FORMAT}
          />
          {selectedRangeGdd !== null && (
            <span className="font-bold"> : {selectedRangeGdd.toFixed(1)}</span>
          )}
        </div>
      </div>

      {averageDifferenceForRange !== null && (
        <div className="flex items-center sm:items-start justify-between mb-3 sm:flex-col">
          <span>
            Growth in average size in selected range :{" "}
            <span className="font-bold">
              {averageDifferenceForRange.toFixed(1)} cm
            </span>
          </span>
        </div>
      )}

      <div className="flex items-center justify-between mb-3 sm:flex-col">
        <div>
          <span>Forecast GDD for next &nbsp;</span>
          <InputNumber
            className="w-16"
            style={{ textAlign: "center" }}
            min={1}
            max={14}
            value={forecastDays}
            onChange={handleGDDForecastChange}
          />
          <span>&nbsp; days : </span>
          {forecastGdd !== null && (
            <span className="font-bold">{forecastGdd.toFixed(1)}</span>
          )}
        </div>
      </div>

      {leafSizeContent}
    </div>
  );
};

export { Forecast };
